import { useMedicalCareContext } from "../../../../contexts/MedicalCareContext";
import { useLocation } from "react-router-dom";
import { useDict } from "../../../../utils/useDict";
import { useFhirData } from "../../../../utils/useFhirData";
import { useInterventionsContext } from "../../../../contexts/InterventionsContext";

export function useFilteredConditions() {
  const { summaryData } = useMedicalCareContext();
  const { state } = useLocation();
  const condition = useFhirData.extractPatientSummaryResource(
    summaryData,
    "Condition"
  );
  return (
    condition?.[0]?.entry
      ?.filter(
        (el: any) =>
          el?.resource?.encounter?.reference?.split("/")[1] ===
          state?.encounterReference?.id
      )
      ?.map((el: any) => {
        return {
          cid10: el?.resource?.code?.coding?.[0]?.code,
          description: el?.resource?.code?.coding?.[0]?.display || "",
          clinicalStatus:
            useDict.clinicalStatus[
              el?.resource?.clinicalStatus?.coding?.[0]?.code
            ] || "",
          verificationStatus:
            useDict.verificationStatus[
              el?.resource?.verificationStatus?.coding?.[0]?.code
            ] || "",
        };
      }) || []
  );
}

export function useFilteredAllergies() {
  const { summaryData } = useMedicalCareContext();
  const { state } = useLocation();
  const allergy = useFhirData.extractPatientSummaryResource(
    summaryData,
    "AllergyIntolerance"
  );
  return (
    allergy?.[0]?.entry
      ?.filter(
        (el: any) =>
          el?.resource?.encounter?.reference?.split("/")[1] ===
          state?.encounterReference?.id
      )
      ?.map((el: any) => {
        return {
          description: el.resource?.code?.text,
          clinicalStatus:
            useDict.clinicalStatus[
              el?.resource?.clinicalStatus?.coding?.[0]?.code
            ] || "",
          verificationStatus:
            useDict.verificationStatus[
              el?.resource?.verificationStatus?.coding?.[0]?.code
            ] || "",
          category:
            useDict.category[el?.resource?.category?.[0]] || "-",
          criticality:
            useDict.criticality[el?.resource?.criticality] || "-",
        };
      }) || []
  );
}

export function useFilteredImmunizations() {
  const { summaryData } = useMedicalCareContext();
  const { state } = useLocation();
  const immunization = useFhirData.extractPatientSummaryResource(
    summaryData,
    "Immunization"
  );
  const observations = useFhirData
    .extractPatientSummaryResource(summaryData, "Observation")?.[0]?.entry;
  const encounterId = state?.encounterReference?.id;
  return (
    immunization?.[0]?.entry
      ?.filter(
        (el: any) =>
          el?.resource?.encounter?.reference?.split("/")[1] === encounterId
      )
      ?.map((el: any) => {
        const description = el?.resource?.vaccineCode?.coding?.[0]?.display;
        const code = el?.resource?.vaccineCode?.coding?.[0]?.code;
        const narrative = observations?.find((observation: any) =>
          observation?.resource.partOf?.[0]?.reference?.includes(el?.resource?.id)
        )?.resource?.valueString;
        return { description, code, narrative };
      }) || []
  );
}

export function useFilteredSelectedInterventions() {
  const { selectedInterventions } = useInterventionsContext();

  function formatarInterventions(selectedInterventions: any) {
    let linhas: string[] = [];
    // Medicação simples
    if (selectedInterventions?.medicationSimple?.length) {
      linhas.push("\nMedicação simples:");
      selectedInterventions.medicationSimple.forEach((item: any, idx: number) => {
        linhas.push(`${idx + 1}. Medicamento: ${item.label || "-"}`);
        linhas.push(`${idx + 1}. Posologia: ${item.form || "-"}`);
        linhas.push(`${idx + 1}. Observação: ${item.note || "-"}`);
      });
    }
    // Medicação especial
    if (selectedInterventions?.medicationSpecial?.length) {
      linhas.push("\nMedicação especial:");
      selectedInterventions.medicationSpecial.forEach((item: any, idx: number) => {
        linhas.push(`${idx + 1}. Medicamento: ${item.label || "-"}`);
        linhas.push(`${idx + 1}. Posologia: ${item.form || "-"}`);
        linhas.push(`${idx + 1}. Observação: ${item.note || "-"}`);
      });
    }
    // Exames
    if (selectedInterventions?.service?.length) {
      linhas.push("\nExames:");
      selectedInterventions.service.forEach((item: any, idx: number) => {
        linhas.push(`${idx + 1}. Exame: ${item.name || "-"}`);
        linhas.push(`${idx + 1}. Condição: ${item.condition?.display || "-"}`);
        linhas.push(`${idx + 1}. Categoria: ${item.category?.label || "-"}`);
        linhas.push(`${idx + 1}. Observação: ${item.note || "-"}`);
      });
    }
    // Procedimentos
    if (selectedInterventions?.procedure?.length) {
      linhas.push("\nProcedimentos:");
      selectedInterventions.procedure.forEach((item: any, idx: number) => {
        linhas.push(`${idx + 1}. Procedimento: ${item.name || "-"}`);
        linhas.push(`${idx + 1}. Observação: ${item.note || "-"}`);
      });
    }
    // Relatórios
    if (selectedInterventions?.certificates?.length) {
      linhas.push("\nRelatórios:");
      selectedInterventions.certificates.forEach((item: any, idx: number) => {
        linhas.push(`${idx + 1}. Relatório: ${item.note || "-"}`);
      });
    }
    return linhas.join("\n");
  }

  return formatarInterventions(selectedInterventions);
} 